"use client"

import { Loader2, RotateCcw } from "lucide-react"

import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog"
import { useTranslations } from "next-intl"
import { TranslationText } from "@/components/i18n/translation-text"



type RestartOnboardingConfirmationDialogProps = {
  mode: "self" | "admin"
  open: boolean
  pending: boolean
  tenantName?: string
  onOpenChange: (open: boolean) => void
  onConfirm: () => void | Promise<void>
}

export function RestartOnboardingConfirmationDialog({
  mode,
  open,
  pending,
  tenantName,
  onOpenChange,
  onConfirm,
}: RestartOnboardingConfirmationDialogProps) {
  const tUi = useTranslations("Onboarding.RestartOnboardingConfirmationDialog")
  const tCommonUi = useTranslations("Common")

  const copy = getRestartConfirmationCopy(mode, tenantName, tUi)

  return (
    <AlertDialog open={open} onOpenChange={onOpenChange}>
      <AlertDialogContent>
        <AlertDialogHeader>
          <AlertDialogTitle>{copy.title}</AlertDialogTitle>
          <AlertDialogDescription asChild>
            <div className="space-y-3">
              <p>{copy.effect}</p>
              <p>
                <span key={tUi("existingAgentsConnectedChannelsPhoneNumbersAndKnowledge")}>{tUi("existingAgentsConnectedChannelsPhoneNumbersAndKnowledge")}</span></p>
            </div>
          </AlertDialogDescription>
        </AlertDialogHeader>
        <AlertDialogFooter>
          <AlertDialogCancel disabled={pending}><span key={tCommonUi("cancel")}>{tCommonUi("cancel")}</span></AlertDialogCancel>
          <AlertDialogAction
            disabled={pending}
            onClick={(event) => {
              event.preventDefault()
              void Promise.resolve(onConfirm()).catch(() => undefined)
            }}
          ><TranslationText>{tUi.rich("tag0Tag0RestartOnboarding", { tag0: () => (pending ? (
              <Loader2 className="mr-2 h-4 w-4 animate-spin" />
            ) : (
              <RotateCcw className="mr-2 h-4 w-4" />
            )) })}</TranslationText></AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  )
}

function getRestartConfirmationCopy(
  mode: RestartOnboardingConfirmationDialogProps["mode"],
  tenantName: string | undefined,
  tUi: ReturnType<typeof useTranslations<"Onboarding.RestartOnboardingConfirmationDialog">>
): { title: string; effect: string } {
  switch (mode) {
    case "self":
      return {
        title: tUi("restartOnboarding"),
        effect: tUi("selfRestartEffect"),
      }
    case "admin":
      return {
        title: tUi("adminRestartTitle", { tenant: tenantName ?? tUi("thisWorkspace") }),
        effect: tUi("adminRestartEffect"),
      }
    default: {
      const exhaustiveMode: never = mode
      return exhaustiveMode
    }
  }
}
